import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Camera, Trash2, ImageIcon } from "lucide-react";
import { api, fmtMoney, fileUrl, errMsg } from "@/lib/api";
import { PageHeader, EmptyState, ImportBadge, DocDate } from "@/components/common";
import { ExpenseDialog } from "@/components/dialogs/ExpenseDialog";
import { useQuick } from "@/components/Layout";
import { Button } from "@/components/ui/button";

export default function Expenses() {
  const quick = useQuick();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const { data: expenses = [] } = useQuery({ queryKey: ["expenses"], queryFn: () => api.get("/expenses").then((r) => r.data) });
  const month = new Date().toISOString().slice(0, 7);
  const thisMonth = expenses.filter((e) => e.date.startsWith(month)).reduce((a, e) => a + e.amount, 0);
  const remove = async (e) => { if (!window.confirm("Delete this expense?")) return; try { await api.delete(`/expenses/${e.id}`); qc.invalidateQueries(); } catch (err) { toast.error(errMsg(err)); } };
  const groups = expenses.reduce((acc, e) => { const k = e.date.slice(0, 7); (acc[k] = acc[k] || []).push(e); return acc; }, {});
  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle={`${fmtMoney(thisMonth)} spent this month`} action={<>
        <Button variant="outline" onClick={quick.receiptPhoto} className="h-10" data-testid="expenses-receipt-photo-button"><Camera size={16} /> Receipt</Button>
        <Button onClick={() => quick.newExpense()} className="btn-orange h-10" data-testid="expenses-new-button"><Plus size={16} /> Expense</Button>
      </>} />
      {expenses.length === 0 ? <EmptyState title="No expenses yet" hint="Snap a receipt to log your first expense." /> : Object.entries(groups).map(([m, list]) => (
        <div key={m} className="mb-5">
          <div className="flex justify-between items-baseline mb-2 px-1"><h3 className="font-bold text-slate-700">{new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h3><span className="text-sm text-slate-500 tabular-nums">{fmtMoney(list.reduce((a, e) => a + e.amount, 0))}</span></div>
          <div className="card-flat divide-y divide-slate-100">
            {list.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-3.5 table-row-hover" onClick={() => setEdit(e)} data-testid="expense-row">
                {e.receipt_file_id ? <img src={fileUrl(e.receipt_file_id)} alt="Receipt" className="w-12 h-12 rounded-lg object-cover border shrink-0" data-testid="expense-receipt-thumb" /> : <div className="w-12 h-12 rounded-lg bg-slate-100 grid place-items-center text-slate-400 shrink-0"><ImageIcon size={18} /></div>}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate flex items-center gap-2">{e.merchant || e.category}<ImportBadge doc={e} /></div>
                  <div className="text-xs text-slate-500 truncate"><DocDate doc={e} dateKey="date" /> | {e.category} | {e.payment_method}</div>
                  {e.description && <div className="text-xs text-slate-400 truncate">{e.description}</div>}
                </div>
                <span className="font-semibold tabular-nums">{fmtMoney(e.amount)}</span>
                <button onClick={(ev) => { ev.stopPropagation(); remove(e); }} className="text-slate-300 hover:text-red-600 p-1" data-testid="expense-delete-button"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
      <ExpenseDialog open={!!edit} onClose={() => setEdit(null)} expense={edit} />
    </div>
  );
}
